import { useEffect, useMemo, useState } from "react";
import CrudPage from "@/components/CrudPage";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Activity, AlertTriangle, Copy, FileText, RefreshCw, RotateCcw, Power, Save, Search, Server, Settings } from "lucide-react";

const fields = [
  { key: "modelo", label: "Modelo", type: "select", options: [{ value: "6921", label: "Cisco 6921" }, { value: "6941", label: "Cisco 6941" }] },
  { key: "tenant_id", label: "Cliente", type: "select", options: () => [], hidden: true },
  { key: "branch_id", label: "Filial", type: "select", options: () => [], hidden: true },
  { key: "mac", label: "MAC", mono: true, placeholder: "001122AABBCC" },
  { key: "ramal", label: "Ramal", mono: true, placeholder: "1001" },
  { key: "senha", label: "Senha SIP", type: "password", mono: true },
  { key: "nome", label: "Nome/Label" },
  { key: "servidor", label: "Servidor SIP/TFTP do aparelho", mono: true, placeholder: "Usar configuracao padrao" },
  { key: "dominio", label: "Dominio DNS/SIP do aparelho", mono: true, placeholder: "Usar configuracao padrao" },
  { key: "porta", label: "Porta SIP do aparelho", type: "number", placeholder: "Usar configuracao padrao" },
  { key: "protocolo", label: "Protocolo", type: "select", options: [{ value: "AUTO", label: "Automatico pelo ramal" }, { value: "UDP", label: "UDP" }, { value: "TCP", label: "TCP" }, { value: "TLS", label: "TLS seguro" }] },
  { key: "enabled", label: "Ativo", type: "toggle" },
];

export default function CiscoProvisioning() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";
  const [rendering, setRendering] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [deviceAction, setDeviceAction] = useState("");
  const [tenants, setTenants] = useState([]);
  const [settings, setSettings] = useState({
    servidor: "sipmbr.voxyra.net.br",
    dominio_dns: "",
    porta: 5060,
    protocolo: "AUTO",
    register_expires: 60,
    register_delta: 15,
  });
  const [monitor, setMonitor] = useState(null);
  const [requestFilter, setRequestFilter] = useState("");
  const [publishedFilter, setPublishedFilter] = useState("");
  const [copiedDiagnostic, setCopiedDiagnostic] = useState("");

  const tenantOptions = tenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.nome,
  }));

  const tenantFor = (tenantId) => (
    tenants.find((tenant) => tenant.id === (tenantId || user?.tenant_id || "")) || null
  );

  const branchOptionsFor = (tenantId) => {
    const tenant = tenantFor(tenantId);
    const branches = Array.isArray(tenant?.branches) ? tenant.branches : [];
    return [
      { value: "", label: "Cliente principal" },
      ...branches
        .filter((branch) => branch?.enabled !== false)
        .map((branch) => ({
          value: branch.id,
          label: branch.nome || branch.id,
        })),
    ];
  };

  const normalizeCiscoForm = (current, row = null) => {
    const effectiveTenantId = current.tenant_id || row?.tenant_id || user?.tenant_id || "";
    const next = {
      ...current,
      tenant_id: effectiveTenantId,
      branch_id: current.branch_id || row?.branch_id || "",
    };
    const options = branchOptionsFor(effectiveTenantId);
    if (!options.some((option) => option.value === next.branch_id)) {
      next.branch_id = "";
    }
    return next;
  };

  const ciscoFields = fields.map((field) => {
    if (field.key === "tenant_id") {
      return {
        ...field,
        hidden: !isSuperAdmin,
        options: () => [{ value: "", label: "Selecione o cliente" }, ...tenantOptions],
        onChange: (next, value) => normalizeCiscoForm({ ...next, tenant_id: value, branch_id: "" }),
      };
    }
    if (field.key === "branch_id") {
      return {
        ...field,
        hidden: false,
        options: (form) => branchOptionsFor(form.tenant_id),
        onChange: (next, value) => ({ ...next, branch_id: value }),
      };
    }
    return field;
  });

  useEffect(() => {
    api.get("/provisioning/cisco/settings")
      .then(({ data }) => setSettings(data))
      .catch((e) => setError(e.response?.data?.detail || "Falha ao carregar configuracao."));
  }, []);

  useEffect(() => {
    api.get("/tenants")
      .then(({ data }) => setTenants(data || []))
      .catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    let active = true;
    const loadMonitor = async () => {
      try {
        const { data } = await api.get("/provisioning/tftp/status", { params: { _ts: Date.now() } });
        if (active) setMonitor(data);
      } catch (e) {
        if (active) setError(e.response?.data?.detail || "Falha ao consultar o TFTP.");
      }
    };
    loadMonitor();
    const timer = setInterval(loadMonitor, 10000);
    return () => { active = false; clearInterval(timer); };
  }, [isSuperAdmin]);

  const filteredRequests = useMemo(() => {
    const term = requestFilter.trim().toLowerCase();
    const rows = monitor?.requests || [];
    if (!term) return rows;
    return rows.filter((row) => {
      const filename = String(row?.filename || "").toLowerCase();
      const ip = String(row?.ip || "").toLowerCase();
      const ramal = String(row?.ramal || "").toLowerCase();
      return filename.includes(term) || ip.includes(term) || ramal.includes(term);
    });
  }, [monitor?.requests, requestFilter]);

  const filteredFiles = useMemo(() => {
    const term = publishedFilter.trim().toLowerCase();
    const rows = monitor?.files || [];
    if (!term) return rows;
    return rows.filter((row) => {
      const name = String(row?.name || "").toLowerCase();
      const ramal = String(row?.ramal || "").toLowerCase();
      return name.includes(term) || ramal.includes(term);
    });
  }, [monitor?.files, publishedFilter]);

  const provisioningDiagnostics = useMemo(() => {
    const requests = monitor?.requests || [];
    if (!requests.length) return [];

    const byTarget = new Map();
    for (const row of requests) {
      const key = row?.mac || row?.ramal || row?.ip || row?.filename;
      if (!key || byTarget.has(key)) continue;

      const filename = String(row?.filename || "");
      const available = Boolean(row?.available);
      const isSecurityOptional = /^(CTL|ITL)SEP/i.test(filename);
      if (available || isSecurityOptional) continue;

      const diagnosis = diagnoseProvisioningFailure(row);
      byTarget.set(key, {
        key,
        mac: row?.mac || "-",
        ramal: row?.ramal || "-",
        ip: row?.ip || "-",
        filename,
        timestamp: row?.timestamp,
        reason: diagnosis.reason,
        level: diagnosis.level,
      });
    }

    return Array.from(byTarget.values()).slice(0, 12);
  }, [monitor?.requests]);

  const saveSettings = async () => {
    setSavingSettings(true); setError(""); setResult("");
    try {
      const { data } = await api.put("/provisioning/cisco/settings", settings);
      setSettings((old) => ({ ...old, ...data }));
      setResult("Configuracao padrao salva e XMLs Cisco existentes atualizados.");
    } catch (e) {
      setError(e.response?.data?.detail || "Falha ao salvar configuracao.");
    } finally {
      setSavingSettings(false);
    }
  };

  const renderAll = async () => {
    setRendering(true); setResult(""); setError("");
    try {
      const { data } = await api.post("/provisioning/cisco/render");
      setResult(`${data.count} arquivo(s) gerado(s) - TFTP ${data.tftp} - DNS/SIP ${(data.domains || []).join(", ")}`);
    } catch (e) {
      setError(e.response?.data?.detail || "Falha ao gerar os arquivos Cisco.");
    } finally {
      setRendering(false);
    }
  };

  const copyDiagnostic = async (row) => {
    const lines = [
      `Horario: ${formatDate(row.timestamp)}`,
      `MAC: ${row.mac}`,
      `Ramal: ${row.ramal}`,
      `Origem: ${row.ip}`,
      `Arquivo: ${row.filename}`,
      `Nivel: ${diagnosticLevelLabel(row.level)}`,
      `Motivo: ${row.reason}`,
    ];
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      setCopiedDiagnostic(row.key);
      window.setTimeout(() => setCopiedDiagnostic(""), 1800);
    } catch (_err) {
      setError("Nao foi possivel copiar o diagnostico neste navegador.");
    }
  };

  return <div className="space-y-5">
    {isSuperAdmin && <section className="space-y-3" data-testid="tftp-monitor">
      <div className="flex items-center justify-between"><div><div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Monitoramento em tempo real</div><h1 className="text-2xl font-semibold">Servidor TFTP</h1></div><span className={`sbc-tag ${monitor?.status === "online" ? "tag-online" : "tag-offline"}`}>{monitor?.status?.toUpperCase() || "CARREGANDO"}</span></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 border border-[color:var(--border-default)] bg-[color:var(--bg-surface)]">
        <Metric icon={Server} label="Servico" value={monitor?.service_active ? "tftpd-hpa ativo" : "Inativo"}/>
        <Metric icon={Activity} label="Porta" value={monitor?.port_listening ? "UDP 69 escutando" : "UDP 69 fechada"}/>
        <Metric icon={FileText} label="Arquivos" value={monitor ? `${monitor.file_count} arquivo(s)` : "-"}/>
        <Metric icon={RefreshCw} label="Ultima requisicao" value={monitor?.last_request ? `${monitor.last_request.ip} - ${monitor.last_request.filename}` : "Nenhuma"}/>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="border border-[color:var(--border-default)] overflow-hidden">
          <div className="px-3 py-2 border-b border-[color:var(--border-default)] text-xs font-semibold">Requisicoes recentes</div>
          <div className="px-3 py-2 border-b border-[color:var(--border-default)]">
            <label className="relative block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                className="sbc-input pl-9"
                placeholder="Filtrar por MAC, ramal, IP ou arquivo"
                value={requestFilter}
                onChange={(e) => setRequestFilter(e.target.value)}
              />
            </label>
          </div>
          <div className="overflow-auto max-h-64">
            <table className="sbc-table">
              <thead><tr><th>Horario</th><th>Origem</th><th>Arquivo</th><th>Resultado</th></tr></thead>
              <tbody>
                {!filteredRequests.length && <tr><td colSpan="4" className="text-center text-[color:var(--text-muted)]">{monitor?.requests?.length ? "Nenhum resultado para o filtro informado" : "Nenhuma requisicao encontrada"}</td></tr>}
                {filteredRequests.map((row, index)=><tr key={`${row.timestamp}-${index}`}><td className="font-mono text-[10px] whitespace-nowrap">{formatDate(row.timestamp)}</td><td><div className="font-mono text-xs">{row.ip}</div>{row.ramal && <div className="text-[10px] text-[color:var(--text-muted)]">Ramal {row.ramal}</div>}</td><td className="font-mono text-[10px] break-all">{row.filename}</td><td><RequestResult row={row}/></td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
        <div className="border border-[color:var(--border-default)] overflow-hidden">
          <div className="px-3 py-2 border-b border-[color:var(--border-default)] text-xs font-semibold">Arquivos publicados</div>
          <div className="px-3 py-2 border-b border-[color:var(--border-default)]">
            <label className="relative block">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]" />
              <input
                className="sbc-input pl-9"
                placeholder="Filtrar por MAC, ramal ou arquivo"
                value={publishedFilter}
                onChange={(e) => setPublishedFilter(e.target.value)}
              />
            </label>
          </div>
          <div className="overflow-auto max-h-64">
            <table className="sbc-table">
              <thead><tr><th>Arquivo</th><th>Ramal</th><th>Tamanho</th><th>Atualizado</th></tr></thead>
              <tbody>
                {!filteredFiles.length && <tr><td colSpan="4" className="text-center text-[color:var(--text-muted)]">{monitor?.files?.length ? "Nenhum resultado para o filtro informado" : "Nenhum arquivo publicado"}</td></tr>}
                {filteredFiles.map((row)=><tr key={row.name}><td className="font-mono text-[10px] break-all">{row.name}</td><td className="font-mono">{row.ramal || "-"}</td><td className="font-mono">{formatBytes(row.size)}</td><td className="font-mono text-[10px] whitespace-nowrap">{formatDate(row.modified_at)}</td></tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="border border-[color:var(--border-default)] overflow-hidden">
        <div className="px-3 py-2 border-b border-[color:var(--border-default)] flex items-center gap-2 text-xs font-semibold">
          <AlertTriangle size={14} className="text-[color:var(--accent-red)]" />
          Erros prováveis de provisionamento
        </div>
        <div className="overflow-auto max-h-64">
          <table className="sbc-table">
            <thead>
              <tr>
                <th>Horario</th>
                <th>MAC</th>
                <th>Ramal</th>
                <th>Origem</th>
                <th>Arquivo</th>
                <th>Motivo provavel</th>
                <th className="text-right">Acao</th>
              </tr>
            </thead>
            <tbody>
              {!provisioningDiagnostics.length && (
                <tr>
                  <td colSpan="7" className="text-center text-[color:var(--text-muted)]">
                    Nenhum erro recente de envio de arquivo encontrado.
                  </td>
                </tr>
              )}
              {provisioningDiagnostics.map((row) => (
                <tr key={`${row.key}-${row.filename}-${row.timestamp}`}>
                  <td className="font-mono text-[10px] whitespace-nowrap">{formatDate(row.timestamp)}</td>
                  <td className="font-mono text-[10px]">{row.mac}</td>
                  <td className="font-mono">{row.ramal}</td>
                  <td className="font-mono text-[10px]">{row.ip}</td>
                  <td className="font-mono text-[10px] break-all">{row.filename}</td>
                  <td>
                    <span className={`sbc-tag ${diagnosticLevelTone(row.level)}`}>{row.reason}</span>
                  </td>
                  <td className="text-right">
                    <button className="sbc-btn" onClick={() => copyDiagnostic(row)} title="Copiar diagnostico">
                      <Copy size={12} />
                      {copiedDiagnostic === row.key ? "Copiado" : "Copiar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>}

    <section className="border border-[color:var(--border-default)] bg-[color:var(--bg-surface)] p-4 space-y-4" data-testid="cisco-default-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3"><Settings size={18} className="text-[color:var(--accent-green)]"/><div><h2 className="text-sm font-semibold">Configuracao padrao dos aparelhos Cisco</h2><p className="text-xs text-[color:var(--text-secondary)]">Aplicada quando o aparelho nao possuir servidor, dominio ou porta individual.</p></div></div>
        <button className="sbc-btn" onClick={saveSettings} disabled={savingSettings}><Save size={14}/>{savingSettings ? "Salvando..." : "Salvar e atualizar XMLs"}</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <label><span className="sbc-label">Servidor SIP/TFTP</span><input className="sbc-input font-mono mt-1" value={settings.servidor || ""} onChange={(e)=>setSettings({...settings, servidor:e.target.value})} placeholder="sipmbr.voxyra.net.br"/></label>
        <label><span className="sbc-label">Dominio DNS/SIP</span><input className="sbc-input font-mono mt-1" value={settings.dominio_dns || ""} onChange={(e)=>setSettings({...settings, dominio_dns:e.target.value})} placeholder="cliente.voxyra.net.br"/></label>
        <label><span className="sbc-label">Porta SIP</span><input type="number" className="sbc-input font-mono mt-1" value={settings.porta || ""} onChange={(e)=>setSettings({...settings, porta:Number(e.target.value)})}/></label>
        <label><span className="sbc-label">Protocolo</span><select className="sbc-input mt-1" value={settings.protocolo || "AUTO"} onChange={(e)=>setSettings({...settings, protocolo:e.target.value})}><option value="AUTO">Automatico pelo ramal</option><option value="UDP">UDP</option><option value="TCP">TCP</option><option value="TLS">TLS seguro</option></select></label>
        <label><span className="sbc-label">Registro (s)</span><input type="number" className="sbc-input font-mono mt-1" value={settings.register_expires || 60} onChange={(e)=>setSettings({...settings, register_expires:Number(e.target.value)})}/></label>
        <label><span className="sbc-label">Antecedencia do novo registro (s)</span><input type="number" className="sbc-input font-mono mt-1" value={settings.register_delta || 15} onChange={(e)=>setSettings({...settings, register_delta:Number(e.target.value)})}/></label>
      </div>
      <div className="text-[11px] text-[color:var(--text-muted)]">Quando a conexao cair, o aparelho continua usando a configuracao carregada; ao salvar aqui, os XMLs TFTP ja publicados tambem sao regenerados.</div>
    </section>

    <div className="flex justify-end"><button className="sbc-btn sbc-btn-primary" onClick={renderAll} disabled={rendering}><RefreshCw size={14} className={rendering ? "animate-spin" : ""}/>Gerar TFTP</button></div>
    {result && <div className="text-xs font-mono text-[color:var(--accent-green)]">{result}</div>}
    {error && <div className="text-xs font-mono text-[color:var(--accent-red)]">{error}</div>}

    <CrudPage
      title="Provisionamento Cisco"
      subtitle="Cisco 6921 / 6941"
      endpoint="/cisco-provisioning"
      testidPrefix="cisco"
      refreshInterval={5000}
      fields={ciscoFields}
      headers={["Modelo", "MAC", "Identidade SIP", "Servidor", "Registro SIP"]}
      defaultValues={{ tenant_id: user?.tenant_id || "", branch_id: "", modelo: "6921", mac: "", ramal: "", senha: "", nome: "", servidor: "", dominio: "", porta: null, protocolo: "AUTO", enabled: true }}
      transformFormOpen={(form, row) => normalizeCiscoForm(form, row)}
      transformSubmit={(form) => normalizeCiscoForm({
        ...form,
        tenant_id: isSuperAdmin ? (form.tenant_id || user?.tenant_id || "") : (user?.tenant_id || form.tenant_id || ""),
      })}
      renderRow={(r) => <>
        <td>{r.modelo}</td>
        <td className="font-mono">{r.mac}</td>
        <td className="font-mono"><div>{r.auth_usuario || r.ramal}</div><div className="text-[10px] text-[color:var(--text-muted)]">Discagem curta: {r.ramal}</div></td>
        <td className="font-mono text-xs">{r.servidor || settings.servidor}:{r.porta || settings.porta}/{r.protocolo}</td>
        <td>{!r.enabled ? <span className="sbc-tag tag-offline">INATIVO</span> : r.registered ? <><span className="sbc-tag tag-online">REGISTRADO</span><div className="mt-1 font-mono text-[10px] text-[color:var(--text-muted)]">{r.registration_ip || "-"} / {r.registration_transport || "-"}</div></> : <span className="sbc-tag tag-offline">DESCONECTADO</span>}</td>
      </>}
      renderActions={(row, { reload }) => <>
        <button className="sbc-btn" title="Recarregar configuracao no aparelho" disabled={!row.registered || deviceAction === row.id} onClick={async () => {
          setDeviceAction(row.id); setError(""); setResult("");
          try {
            const { data } = await api.post(`/provisioning/cisco/${row.id}/resync`);
            setResult(`Recarga de configuracao enviada para ${data.auth_user} em ${data.contact_ip}:${data.contact_port}`);
            await reload(true);
          } catch (e) {
            setError(e.response?.data?.detail || "Falha ao recarregar a configuracao do Cisco.");
          } finally {
            setDeviceAction("");
          }
        }}><RotateCcw size={12} /></button>
        <button className="sbc-btn" title="Reset Cisco (experimental)" disabled={!row.registered || deviceAction === row.id} onClick={async () => {
          if (!window.confirm(`Executar reset experimental no Cisco do ramal ${row.ramal}?`)) return;
          setDeviceAction(row.id); setError(""); setResult("");
          try {
            const { data } = await api.post(`/provisioning/cisco/${row.id}/reboot`);
            setResult(`Reset experimental enviado para ${data.auth_user} em ${data.contact_ip}:${data.contact_port}`);
            await reload(true);
          } catch (e) {
            setError(e.response?.data?.detail || "Falha ao enviar o reset experimental do Cisco.");
          } finally {
            setDeviceAction("");
          }
        }}><Power size={12} /></button>
      </>}
    />
  </div>;
}

function Metric({ icon: Icon, label, value }) {
  return <div className="min-h-20 p-3 border-r border-b border-[color:var(--border-default)] flex items-center gap-3"><Icon size={17} className="text-[color:var(--accent-green)] shrink-0"/><div className="min-w-0"><div className="sbc-label">{label}</div><div className="text-xs font-mono truncate" title={value}>{value}</div></div></div>;
}

function RequestResult({ row }) {
  if (row.available) return <span className="sbc-tag tag-online">ENTREGUE</span>;
  if (/^(CTL|ITL)SEP/i.test(row.filename || "")) return <span className="sbc-tag tag-offline" title="Arquivo de seguranca opcional em provisionamento sem CUCM">OPCIONAL</span>;
  return <span className="sbc-tag tag-offline">NAO ENCONTRADO</span>;
}

function diagnoseProvisioningFailure(row) {
  const filename = String(row?.filename || "").toUpperCase();
  if (/^SEP[0-9A-F]{12}\.CNF\.XML(\.SGN)?$/.test(filename)) {
    return { level: "critical", reason: "XML principal ausente ou MAC diferente do provisionado" };
  }
  if (/^XMLDEFAULT\.CNF\.XML(\.SGN)?$/.test(filename)) {
    return { level: "critical", reason: "Arquivo padrao do Cisco ausente" };
  }
  if (/^DIALPLAN\.XML$/i.test(filename)) {
    return { level: "high", reason: "Dialplan padrao nao publicado" };
  }
  if (/^ITLFILE\.TLV$/i.test(filename) || /^CTLSEP/i.test(filename) || /^ITLSEP/i.test(filename)) {
    return { level: "medium", reason: "Arquivo de seguranca/firmware solicitado e nao encontrado" };
  }
  if (/\.LOADS$/i.test(filename) || /\.(SBN|COP|ZUP|BIN)$/i.test(filename)) {
    return { level: "high", reason: "Pacote de firmware ausente no TFTP" };
  }
  return { level: "medium", reason: "Arquivo requisitado nao esta publicado no TFTP" };
}

function diagnosticLevelTone(level) {
  if (level === "critical") return "tag-danger";
  if (level === "high") return "tag-warn";
  if (level === "medium") return "tag-offline";
  return "";
}

function diagnosticLevelLabel(level) {
  if (level === "critical") return "Critico";
  if (level === "high") return "Alto";
  if (level === "medium") return "Medio";
  return "Info";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("pt-BR", { hour12: false });
}

function formatBytes(value) {
  const bytes = Number(value || 0);
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}
